'use client'

import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { getGeocode, getLatLng } from 'use-places-autocomplete'
import { t } from '@lingui/core/macro'

import { useGetMeetingLinkQuery } from '@/core/graphql/hooks'
import { AvailabilityMeetingPlace } from '@/core/graphql/types'

import GoogleMap, { GoogleMapHandle } from '@/shared/ui/maps/GoogleMap'

import HospitalIconSrc from '@/assets/img/hopital.png'

import { useBooking } from '../model/bookingContext'
import { isOutsideServiceArea } from '../lib/serviceArea'

function getIconUrl(imported: string | { src: string }): string {
  return typeof imported === 'string' ? imported : imported.src
}

function hospitalIcon() {
  return {
    anchor: new google.maps.Point(15, 15),
    origin: new google.maps.Point(0, 0),
    scaledSize: new google.maps.Size(30, 30),
    size: new google.maps.Size(30, 30),
    url: getIconUrl(HospitalIconSrc),
  }
}

function patientIcon() {
  return {
    path: google.maps.SymbolPath.CIRCLE,
    fillColor: '#E53935',
    fillOpacity: 1,
    strokeColor: '#B71C1C',
    strokeWeight: 2,
    scale: 8,
  }
}

export type MapProps = {
  meetingLinkName: string
}

export default function Map(props: MapProps) {
  const { meetingLinkName } = props

  const googleMapRef = useRef<GoogleMapHandle>(null)
  const patientMarkerId = useRef<string | null>(null)

  const [loaded, setLoaded] = useState(false)
  const [insideZone, setInsideZone] = useState<boolean | null>(null)

  const { data } = useGetMeetingLinkQuery({ variables: { slug: meetingLinkName } })
  const { state } = useBooking()

  const meetingLink = data?.meetingLink

  // Collect center establishment placeIds
  const establishmentPlaces = useMemo(() => {
    const places: string[] = []
    if (!meetingLink?.availabilities) return places
    for (const availability of meetingLink.availabilities) {
      if (availability.address?.placeId && availability.meetingPlace === AvailabilityMeetingPlace.Center) {
        if (!places.includes(availability.address.placeId)) {
          places.push(availability.address.placeId)
        }
      }
    }
    return places
  }, [meetingLink])

  // Collect AT_HOME service areas
  const serviceAreas = useMemo(() => {
    if (!meetingLink?.availabilities) return []
    return meetingLink.availabilities
      .filter(
        (a) =>
          a.meetingPlace === AvailabilityMeetingPlace.AtHome &&
          a.serviceArea?.center?.lat != null &&
          a.serviceArea?.center?.lng != null &&
          a.serviceArea?.radiusKm
      )
      .map((a, i) => ({
        id: `service-area-${i}`,
        center: { lat: a.serviceArea!.center!.lat, lng: a.serviceArea!.center!.lng },
        radiusKm: a.serviceArea!.radiusKm!,
        raw: a.serviceArea!,
      }))
  }, [meetingLink])

  const handleLoad = useCallback(() => {
    setLoaded(true)
  }, [])

  // Show center establishment markers
  useEffect(() => {
    if (!loaded || !establishmentPlaces.length) return

    for (const placeId of establishmentPlaces) {
      getGeocode({ placeId })
        .then((results) => getLatLng(results[0]))
        .then(({ lat, lng }) => {
          if (!googleMapRef.current) return
          const marker = googleMapRef.current.createMarker(lat, lng, hospitalIcon())
          googleMapRef.current.addMarker(placeId, marker)
        })
        .catch(console.error)
    }
  }, [loaded, establishmentPlaces])

  // Show service area circles for AT_HOME availabilities
  useEffect(() => {
    if (!loaded || !googleMapRef.current) return

    for (const area of serviceAreas) {
      googleMapRef.current.addCircle(area.id, area.center, area.radiusKm)
    }

    return () => {
      for (const area of serviceAreas) {
        googleMapRef.current?.removeCircle(area.id)
      }
    }
  }, [loaded, serviceAreas])

  // Show patient location marker
  const patientLat = state.patientLocation?.coordinates?.lat
  const patientLng = state.patientLocation?.coordinates?.lng
  const patientPostalCode = state.patientLocation?.postalCode

  useEffect(() => {
    if (!loaded || !googleMapRef.current) return

    // Remove previous patient marker
    if (patientMarkerId.current) {
      googleMapRef.current.removeMarker(patientMarkerId.current)
      patientMarkerId.current = null
    }

    if (patientLat != null && patientLng != null) {
      const marker = googleMapRef.current.createMarker(patientLat, patientLng, patientIcon(), 5)
      const id = `patient-${patientLat}-${patientLng}`
      googleMapRef.current.addMarker(id, marker)
      patientMarkerId.current = id

      // Check if patient is inside any service area
      if (serviceAreas.length > 0) {
        const outside = serviceAreas.every((area) => isOutsideServiceArea(area.raw, patientPostalCode, { lat: patientLat, lng: patientLng }))
        setInsideZone(!outside)
      } else {
        setInsideZone(null)
      }
    } else {
      setInsideZone(null)
    }
  }, [loaded, patientLat, patientLng, patientPostalCode, serviceAreas])

  return (
    <div className="h-full flex flex-col">
      <GoogleMap onLoad={handleLoad} ref={googleMapRef} />
      {insideZone !== null && (
        <div className={`px-4 py-2 text-sm font-medium text-center ${insideZone ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
          {insideZone ? `✓ ${t`Your address is within the coverage area`}` : `✗ ${t`Your address is outside the coverage area`}`}
        </div>
      )}
    </div>
  )
}
